import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  Skeleton,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Inbox, Mail } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

import {
  acceptReviewItem,
  fetchReviewQueue,
  rejectReviewItem,
  rerouteReviewItem,
  type ReviewQueueItem,
} from '../../../api/ai.js';
import { ApiError } from '../../../lib/api-client.js';

/**
 * `/ai/review-queue` — review the inbound vendor emails the parser was
 * not confident enough to auto-commit. The list shows the AI-suggested
 * match plus the email summary; per-row controls accept the suggestion,
 * reroute to a different WO, or reject the email outright.
 */
export function ReviewQueuePage({ embedded = false }: { embedded?: boolean }) {
  const queueQuery = useQuery({
    queryKey: ['ai-review-queue'],
    queryFn: fetchReviewQueue,
  });

  return (
    <>
      {embedded ? null : (
        <PageHeader
          title="AI inbox"
          description="Vendor emails the AI matched to a work order — accept the suggestion, reroute, or reject."
        />
      )}

      {queueQuery.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : queueQuery.isError ? (
        <ErrorState description="Could not load the review queue." />
      ) : !queueQuery.data || queueQuery.data.items.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="Inbox is clear"
          description="When SiteWise is unsure which work order a vendor email belongs to, it lands here for a human to confirm."
        />
      ) : (
        <div className="space-y-3">
          {queueQuery.data.items.map((item) => (
            <ReviewQueueRow key={item.id} item={item} />
          ))}
        </div>
      )}
    </>
  );
}

interface ReviewQueueRowProps {
  item: ReviewQueueItem;
}

function ReviewQueueRow({ item }: ReviewQueueRowProps) {
  const queryClient = useQueryClient();
  const [rerouteOpen, setRerouteOpen] = useState(false);
  const [targetWoNumber, setTargetWoNumber] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['ai-review-queue'] });
  };

  const acceptMutation = useMutation({
    mutationFn: () => {
      if (!item.suggestedWorkOrderId) {
        throw new Error('No suggested work order to accept');
      }
      return acceptReviewItem(item.id, { workOrderId: item.suggestedWorkOrderId });
    },
    onSuccess: () => {
      toast.success('Routed to work order');
      invalidate();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not accept'),
  });

  const rerouteMutation = useMutation({
    mutationFn: () => {
      const target = targetWoNumber.trim();
      if (!UUID_RE.test(target)) {
        throw new Error('Work order ID must be a UUID');
      }
      return rerouteReviewItem(item.id, { workOrderId: target });
    },
    onSuccess: () => {
      toast.success('Rerouted');
      setRerouteOpen(false);
      setTargetWoNumber('');
      invalidate();
    },
    onError: (err) =>
      toast.error(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Could not reroute',
      ),
  });

  const rejectMutation = useMutation({
    mutationFn: () =>
      rejectReviewItem(item.id, {
        reason: rejectReason.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success('Email rejected');
      setRejectOpen(false);
      setRejectReason('');
      invalidate();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not reject'),
  });

  const confidencePct =
    item.confidence == null ? null : `${Math.round(item.confidence * 100)}%`;

  return (
    <Card>
      <CardHeader className="space-y-1">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-sm font-semibold truncate">
              <Mail className="mr-1.5 inline h-3.5 w-3.5 align-text-bottom" />
              {item.subject || '(no subject)'}
            </CardTitle>
            <p className="text-xs text-fg-muted">
              From {item.fromEmail} · {new Date(item.receivedAt).toLocaleString()}
            </p>
          </div>
          {confidencePct ? (
            <span className="text-xs text-fg-muted whitespace-nowrap">
              {confidencePct} confident
            </span>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {item.summary ? (
          <p className="rounded bg-surface-muted px-3 py-2 italic text-fg-secondary">
            {item.summary}
          </p>
        ) : null}

        {item.suggestedWorkOrderId ? (
          <div className="rounded border border-border bg-surface px-3 py-2">
            <div className="text-xs text-fg-muted">Suggested match</div>
            <Link
              to={`/work-orders/${item.suggestedWorkOrderId}`}
              className="text-sm font-medium text-primary hover:underline"
            >
              {item.suggestedWorkOrderNumber ?? '—'} · {item.suggestedWorkOrderTitle ?? ''}
            </Link>
            {item.suggestedAction ? (
              <div className="mt-0.5 text-xs text-fg-muted">
                Action: {item.suggestedAction}
              </div>
            ) : null}
          </div>
        ) : (
          <p className="text-xs text-fg-muted">
            No confident match — pick a work order or reject below.
          </p>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          {item.suggestedWorkOrderId ? (
            <Button
              size="sm"
              onClick={() => acceptMutation.mutate()}
              disabled={acceptMutation.isPending}
            >
              Accept suggestion
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setRerouteOpen((v) => !v)}
          >
            Reroute…
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setRejectOpen((v) => !v)}
          >
            Reject…
          </Button>
        </div>

        {rerouteOpen ? (
          <div className="space-y-2 rounded border border-border bg-surface-muted p-3">
            <div className="text-xs font-medium text-fg">Reroute to work order</div>
            <Input
              value={targetWoNumber}
              onChange={(e) => setTargetWoNumber(e.target.value)}
              placeholder="Work order ID (UUID)"
              className="font-mono text-xs"
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => rerouteMutation.mutate()}
                disabled={rerouteMutation.isPending || !targetWoNumber.trim()}
              >
                Reroute
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setRerouteOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : null}

        {rejectOpen ? (
          <div className="space-y-2 rounded border border-border bg-surface-muted p-3">
            <div className="text-xs font-medium text-fg">Reject email</div>
            <Input
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Optional reason (audit only)"
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="destructive"
                onClick={() => rejectMutation.mutate()}
                disabled={rejectMutation.isPending}
              >
                Reject
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setRejectOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
